const sorters = {
  latest: (a, b) => new Date(b.published) - new Date(a.published),
  popular: (a, b) => (b.plays || 0) - (a.plays || 0),
  name: (a, b) => a.title.localeCompare(b.title),
};

const clamp = (value, min, max) => {
  if (Number.isNaN(value)) return min;
  if (max < min) return min;
  return Math.min(Math.max(value, min), max);
};

export default class GameStore {
  constructor(games = [], options = {}) {
    this.games = games;
    this.state = {
      category: options.category || 'all',
      sort: options.sort || 'latest',
      search: options.search || '',
      page: options.page || 1,
      perPage: options.perPage || 32,
    };
    this.listeners = new Set();
    this.state = { ...this.state, page: this._clampPage(this.state) };
  }

  subscribe(fn) {
    this.listeners.add(fn);
    fn(this.getSnapshot());
    return () => this.listeners.delete(fn);
  }

  setState(patch) {
    const nextState = { ...this.state, ...patch };
    const page = this._clampPage(nextState);
    this.state = { ...nextState, page };
    this.emit();
  }

  resetFilters() {
    this.setState({ category: 'all', search: '', page: 1 });
  }

  setCategory(category) {
    this.setState({ category, page: 1 });
  }

  setSearch(search) {
    this.setState({ search, page: 1 });
  }

  setSort(sort) {
    this.setState({ sort, page: 1 });
  }

  setPage(page) {
    const target = Number.parseInt(page, 10);
    if (Number.isNaN(target)) return;
    this.setState({ page: target });
  }

  nextPage() {
    this.setState({ page: this.state.page + 1 });
  }

  prevPage() {
    this.setState({ page: this.state.page - 1 });
  }

  getFilteredGames(state = this.state) {
    const { category, search } = state;
    let filtered = [...this.games];

    if (category && category !== 'all') {
      filtered = filtered.filter(game => (game.categories || []).includes(category));
    }

    if (search) {
      const token = search.toLowerCase();
      filtered = filtered.filter(game => {
        const haystack = [game.title, ...(game.tags || []), ...(game.categories || [])]
          .join(' ')
          .toLowerCase();
        return haystack.includes(token);
      });
    }

    return filtered;
  }

  getSortedGames(state = this.state) {
    const sorter = sorters[state.sort] || sorters.latest;
    return this.getFilteredGames(state).slice().sort(sorter);
  }

  getVisibleGames(state = this.state) {
    const sorted = this.getSortedGames(state);
    const start = (state.page - 1) * state.perPage;
    const end = start + state.perPage;
    return sorted.slice(start, end);
  }

  getTotalPages(state = this.state) {
    const total = this.getFilteredGames(state).length;
    if (total === 0) return 0;
    return Math.ceil(total / state.perPage);
  }

  hasMore(state = this.state) {
    const totalPages = this.getTotalPages(state);
    return totalPages > 0 && state.page < totalPages;
  }

  getSnapshot() {
    const total = this.getFilteredGames().length;
    const totalPages = this.getTotalPages();
    return {
      state: { ...this.state },
      total,
      totalPages,
      visible: this.getVisibleGames(),
      hasMore: this.hasMore(),
    };
  }

  emit() {
    const snapshot = this.getSnapshot();
    this.listeners.forEach(listener => listener(snapshot));
  }

  _clampPage(state) {
    const totalPages = this.getTotalPages(state);
    if (totalPages === 0) return 1;
    return clamp(state.page, 1, totalPages);
  }
}
